import { businessDaysBetween } from './ltdEcdUtils';

const finiteHour = (value) => {
    if (value == null || value === '') return null;
    const hours = Number(value);
    return Number.isFinite(hours) ? hours : null;
};

const achievableUpDate = (row) => {
    if (row.cannibalizeEcd && !row.cannibalizeHasUnknown) return row.cannibalizeEcd;
    if (row.finalEcd && !row.hasUnknownEcd) return row.finalEcd;
    return null;
};

const summedManHours = (partHours) => {
    if (!partHours || partHours.length === 0) return null;
    let total = 0;
    for (const value of partHours) {
        const hours = finiteHour(value);
        if (hours == null) return null;
        total += hours;
    }
    return total;
};

const byBuno = (a, b) => String(a.buno || '').localeCompare(String(b.buno || ''));

/**
 * Rank aircraft by soonest fully planned UP date, then fewest man-hours.
 * Incomplete schedules stay after the ranked set, ordered by fewest missing hours.
 * Days down is ignored.
 *
 * @param {Array<{
 *   buno: string,
 *   ak0Count?: number,
 *   finalEcd?: Date|null,
 *   hasUnknownEcd?: boolean,
 *   cannibalizeEcd?: Date|null,
 *   cannibalizeHasUnknown?: boolean,
 *   partHours?: Array<number|null>,
 *   missingManHours?: number,
 * }>} rows
 * @param {Date} today
 */
export const rankLtdAircraft = (rows, today) => {
    const enriched = (rows || []).map(row => {
        const upDate = achievableUpDate(row);
        const manHoursToUp = upDate ? summedManHours(row.partHours) : null;
        const daysToUp = manHoursToUp == null ? null : businessDaysBetween(today, upDate);
        const rankable = daysToUp != null && manHoursToUp != null;
        return {
            ...row,
            daysToUp: rankable ? daysToUp : null,
            manHoursToUp: rankable ? manHoursToUp : null,
            rank: null,
            focusOrder: 0,
        };
    });

    const rankable = enriched.filter(row => row.daysToUp != null);
    const unranked = enriched.filter(row => row.daysToUp == null);

    rankable.sort((a, b) => {
        if (a.daysToUp !== b.daysToUp) return a.daysToUp - b.daysToUp;
        if (a.manHoursToUp !== b.manHoursToUp) return a.manHoursToUp - b.manHoursToUp;
        const ak0Diff = (a.ak0Count || 0) - (b.ak0Count || 0);
        if (ak0Diff !== 0) return ak0Diff;
        return byBuno(a, b);
    });
    rankable.forEach((row, index) => {
        row.rank = index + 1;
    });

    unranked.sort((a, b) => {
        const missingDiff = (a.missingManHours ?? Number.MAX_SAFE_INTEGER)
            - (b.missingManHours ?? Number.MAX_SAFE_INTEGER);
        if (missingDiff !== 0) return missingDiff;
        return byBuno(a, b);
    });

    const ordered = [...rankable, ...unranked];
    ordered.forEach((row, index) => {
        row.focusOrder = index;
    });
    return ordered;
};
